// Sport derivation + filter matching, shared by the Positions page and the
// org-wide Org-Account view so both filter rows identically. Extracted verbatim
// from PositionTracker's inline copy.
import { parseTicker } from './parse';
import { SPORT_TABS, type Sport } from '../../views/ValueDashboard/types';

export type SportFilterMode = 'include' | 'exclude';
export type SportFilter = ReadonlyMap<Sport, SportFilterMode>;

/** Normalize a loose sport string (hydra's `sport:"SOCCER"`, a ticker
 *  category, a PM slug segment) to a Sport id. Exported so callers that
 *  resolve a row's sport from the live frame reuse the SAME coercion the
 *  ticker path uses — two spellings of "is this soccer" is how rows go
 *  missing from filters. */
export function coerceSport(raw: string | null | undefined): Sport | null {
  if (!raw) return null;
  const sport = raw.toLowerCase();
  if (SPORT_TABS.some((tab) => tab.id === sport)) return sport as Sport;
  if (sport === 'ncaa' || sport === 'ncaamb' || sport === 'ncaaw' || sport === 'ncaawb') {
    return 'cbb';
  }
  return null;
}

export function sportFromTicker(ticker: string): Sport | null {
  const parsed = parseTicker(ticker);
  if (parsed.category === 'polymarket') {
    const seriesSport = parsed.series.startsWith('PM-') ? parsed.series.slice(3) : null;
    return coerceSport(seriesSport);
  }

  const categoryPrefix = parsed.category.split('-')[0];
  if (categoryPrefix === 'ncaa') return 'cbb';
  if (categoryPrefix === 'tennis') {
    const series = parsed.series.toUpperCase();
    if (series.includes('ITFW')) return 'wta';
    if (series.includes('ITF')) return 'atp';
    if (series.includes('WTA')) return 'wta';
    if (series.includes('ATP')) return 'atp';
    return null;
  }
  const categorySport = coerceSport(categoryPrefix);
  if (categorySport) return categorySport;

  const upper = ticker.toUpperCase();
  if (upper.startsWith('KXWNB')) return 'wnba';
  if (upper.startsWith('KXNBA')) return 'nba';
  if (upper.startsWith('KXNHL')) return 'nhl';
  // Every KXNFL* series is NFL. Without this, a series parseTicker does not know (KXNFLTEAMTOTAL) had no sport and
  // the Book showed "?" (Paine, 2026-09-25).
  if (upper.startsWith('KXNFL')) return 'nfl';
  if (upper.startsWith('KXMLB')) return 'mlb';
  // CFB is KXNCAAF*, a sibling of (not a child of) the KXNCAA basketball
  // families. Keep this before the generic KXNCAA fallback so newly introduced
  // CFB bet types do not show under college basketball.
  if (upper.startsWith('KXNCAAF')) return 'cfb';
  if (upper.startsWith('KXNCAA')) return 'cbb';
  if (upper.startsWith('KXATP')) return 'atp';
  if (upper.startsWith('KXWTA')) return 'wta';
  if (upper.startsWith('KXUFC')) return 'ufc';
  // Tournament-winner futures carry no matchup grammar, so nothing above
  // classifies them and they fell through as null — which made every
  // include-mode sport chip hide the World Cup winner card. Frames stamp
  // the sport, but a settled or not-yet-ingested row has no frame.
  if (upper.startsWith('KXMENWORLDCUP')) return 'soccer';
  if (upper.startsWith('KXWOMENWORLDCUP')) return 'soccer';
  if (
    upper.startsWith('KXEPL') ||
    upper.startsWith('KXLALIGA') ||
    upper.startsWith('KXBUNDESLIGA') ||
    upper.startsWith('KXMLS') ||
    upper.startsWith('KXUEL') ||
    upper.startsWith('KXUCL') ||
    upper.startsWith('KXUECL') ||
    upper.startsWith('KXWC') ||
    upper.startsWith('KXINTLFRIENDLY') ||
    upper.startsWith('KXLIGAMX') ||
    upper.startsWith('KXLIGAEXP') ||
    upper.startsWith('KXCONCACAFNL') ||
    upper.startsWith('KXUEFANL') ||
    upper.startsWith('KXDIMAYOR')
  ) {
    return 'soccer';
  }
  if (upper.startsWith('PM-')) return coerceSport(upper.split('-')[1]);
  return null;
}

export function sportMatchesFilter(sport: Sport | null, filter: SportFilter): boolean {
  if (filter.size === 0) return true;
  const mode = sport != null ? filter.get(sport) : undefined;
  if (mode === 'exclude') return false;
  let hasInclude = false;
  for (const m of filter.values()) {
    if (m === 'include') {
      hasInclude = true;
      break;
    }
  }
  // Exclude-only mode shows everything that isn't excluded — including rows
  // whose sport can't be derived from the ticker.
  return hasInclude ? mode === 'include' : true;
}

/** 3-click cycle per chip: off → include → exclude → off. */
export function cycleSportFilter(prev: SportFilter, sport: Sport): SportFilter {
  const next = new Map(prev);
  const cur = next.get(sport);
  if (cur === undefined) next.set(sport, 'include');
  else if (cur === 'include') next.set(sport, 'exclude');
  else next.delete(sport);
  return next;
}
